/**
 * Where a new employment starts, and when probation ends — Keka's model (UX-A7a / EMP-10).
 *
 * In Keka someone is on probation only when a probation policy or a probation end date applies;
 * otherwise they are simply a confirmed employee. Bulk import carries "In Probation" (yes/no) and
 * "Probation End Date"; adding one employee shows the default probation policy. A person whose
 * joining date hasn't come yet is `pre_joining` — and `pre_joining` is the one status payroll,
 * tax, attendance and assets skip, which is why nobody may be left in it after their joining day.
 *
 * Pure, and shared by Add Employee, bulk import, and the daily job that moves joiners on their
 * joining day, so the three can never disagree. All dates are `YYYY-MM-DD` calendar dates; "today"
 * is the caller's job to resolve in the right timezone (CLAUDE.md invariant 3).
 */

export const PROBATION_DURATION_UNITS = ['days', 'weeks', 'months'] as const;
export type ProbationDurationUnit = (typeof PROBATION_DURATION_UNITS)[number];

/**
 * The longest probation each unit allows: two years, however it is written. One shared number for
 * the contract and the form; 730 months used to pass because one cap applied to every unit.
 */
export const PROBATION_MAX_BY_UNIT = { days: 730, weeks: 104, months: 24 } as const satisfies Record<
  (typeof PROBATION_DURATION_UNITS)[number],
  number
>;

export interface ProbationLength {
  value: number;
  unit: ProbationDurationUnit;
}

/** `date` + a probation length, as a calendar date. Months clamp to the month's last day. */
export function addProbationLength(date: string, length: ProbationLength): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  if (length.unit === 'months') {
    const target = new Date(Date.UTC(y, m - 1 + length.value, 1));
    const lastDay = new Date(
      Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
    ).getUTCDate();
    target.setUTCDate(Math.min(d, lastDay));
    return target.toISOString().slice(0, 10);
  }
  const days = length.unit === 'weeks' ? length.value * 7 : length.value;
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export interface EmploymentStartInput {
  joinedOn: string;
  /** Today in the employee's timezone. */
  today: string;
  /** The import's "In Probation" column / the form's choice: true, false, or not said. */
  inProbation?: boolean;
  /** An explicit probation end date (import column or form). Wins over the policy's length. */
  probationEndOn?: string;
  /** The probation policy that applies (chosen, or the org default) — none means no probation. */
  policy?: ProbationLength | null;
}

export type StartingStatus = 'pre_joining' | 'probation' | 'confirmed';

export interface EmploymentStart {
  status: StartingStatus;
  /** Last day of probation, or null when the person isn't on probation at all. */
  probationEndOn: string | null;
  /** Set once confirmed: the day after probation ended, or the joining date with no probation. */
  confirmedOn: string | null;
}

function dayAfter(date: string): string {
  return addProbationLength(date, { value: 1, unit: 'days' });
}

/**
 * The starting status and probation dates.
 *
 * - "In Probation: No" means no probation, whatever the policy.
 * - Otherwise the end date is the explicit one, else joining date + the policy's length; with
 *   neither, there is no probation (Keka: no policy, no probation).
 * - Joining date still ahead → `pre_joining` (probation dates are kept for the day they join).
 * - Joined, and probation's last day is today or later → `probation`; otherwise → `confirmed`.
 */
export function employmentStart(input: EmploymentStartInput): EmploymentStart {
  const probationEndOn =
    input.inProbation === false
      ? null
      : (input.probationEndOn ??
        (input.policy ? addProbationLength(input.joinedOn, input.policy) : null));

  if (input.joinedOn > input.today) {
    return { status: 'pre_joining', probationEndOn, confirmedOn: null };
  }
  if (probationEndOn && probationEndOn >= input.today) {
    return { status: 'probation', probationEndOn, confirmedOn: null };
  }
  return {
    status: 'confirmed',
    probationEndOn,
    confirmedOn: probationEndOn ? dayAfter(probationEndOn) : input.joinedOn,
  };
}
